import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadFontAsBase64 } from '../loadFont';
import { buildReportPdf } from './reportTheme';
import AppConst from '../apis/appconst';

const PublicReport = () => {
  const { token } = useParams();
  const [state, setState] = useState({ phase: 'verify', msg: '', due: 0 });
  const [verify, setVerify] = useState('');

  const fetchReport = async (code) => {
    setState({ phase: 'loading', msg: '' });
    try {
      const res = await axios.get(
        `${AppConst.BASE_URL}get_public_report?token=${encodeURIComponent(token)}&verify=${encodeURIComponent(code || '')}`
      );
      const d = res.data;

      if (!d.status) {
        setState({ phase: d.reason || 'error', msg: d.message || 'Unable to load report', due: d.due || 0 });
        return;
      }




       // reuse the same config endpoint the internal pages use
      let imgs = { header: '', watermark: '', footer: '' };
      try {
        const cfg = await axios.post(`${AppConst.BASE_URL}${AppConst.getconfig}?config_type=lab`);
        if (cfg.data && cfg.data.status) {
          imgs = {
            header:    cfg.data.header_logo || '',
            watermark: cfg.data.logo || '',
            footer:    cfg.data.footer_logo || ''
          };
        }
      } catch (e) { console.warn('config fetch failed', e); }
      console.log('PUBLIC IMGS:', imgs);  


      const doc = await buildReportPdf({
        jsPDF, autoTable, loadFontAsBase64,
        tests: d.records,
        patient: d.patient,
        printTypeOpt: 'with',
        departs: d.departs || [],
        signData: d.signData || {},
        userName: '',
        reportedOn: d.records?.[0]?.report_info?.test_report_datetime || null,
         images: imgs, 
      });

      const url = URL.createObjectURL(doc.output('blob'));
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${d.patient.lab_reg_uhid || 'lab'}.pdf`;
      a.click();
     URL.revokeObjectURL(url);  
      setState({ phase: 'done', msg: '' });
    } catch (e) {
      console.error(e);
      setState({ phase: 'error', msg: 'Could not reach the server. Please try again.' });
    }
  };

  useEffect(() => { fetchReport(''); /* eslint-disable-next-line */ }, [token]);

   const S = {
    page: {
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(160deg,#eef2f7 0%,#dde7f2 100%)',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif', padding: 16
    },
    card: {
      width: '100%', maxWidth: 400, background: '#fff', borderRadius: 16,
      boxShadow: '0 10px 40px rgba(16,36,70,.12)', overflow: 'hidden'
    },
    head: { background: '#405189', color: '#fff', padding: '26px 24px', textAlign: 'center' },
    body: { padding: '28px 24px', textAlign: 'center' },
    otp: {
      width: 170, padding: '14px 0', fontSize: 26, fontWeight: 600, textAlign: 'center',
      letterSpacing: 10, textIndent: 10, border: '2px solid #ced4da', borderRadius: 10, outline: 'none'
    },
    btn: (on) => ({
      marginTop: 20, width: '100%', padding: '13px 0', fontSize: 16, fontWeight: 600,
      color: '#fff', background: on ? '#405189' : '#aeb7cd', border: 'none',
      borderRadius: 10, cursor: on ? 'pointer' : 'not-allowed', transition: 'background .2s'
    }),
    note: { marginTop: 18, fontSize: 12.5, color: '#8a93a5', lineHeight: 1.6 },
    spin: {
      width: 42, height: 42, margin: '0 auto 18px',
      border: '4px solid #e3e8f0', borderTopColor: '#405189',
      borderRadius: '50%', animation: 'pr-spin .8s linear infinite'
    }
  };

  const Shell = ({ accent = '#405189', icon, title, children }) => (
    <div style={S.page}>
      <style>{`@keyframes pr-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={S.card}>
        <div style={{ ...S.head, background: accent }}>
          {icon && <div style={{ fontSize: 34, lineHeight: 1, marginBottom: 8 }}>{icon}</div>}
          <div style={{ fontSize: 19, fontWeight: 600 }}>{title}</div>
          <div style={{ fontSize: 12.5, opacity: .85, marginTop: 4 }}>Investigation Report</div>
        </div>
        <div style={S.body}>{children}</div>
      </div>
    </div>
  );

  if (state.phase === 'loading')
    return (
      <Shell title="Please wait">
        <div style={S.spin} />
        <div style={{ color: '#5b6478' }}>Preparing your report…</div>
      </Shell>
    );

   if (state.phase === 'verify')
    return (
      <Shell icon="🔒" title="Verify your identity">
        <p style={{ color: '#5b6478', marginTop: 0, marginBottom: 22, fontSize: 14.5 }}>
          Enter your <strong>registered mobile number</strong> to view your report.
        </p>
        <input
          value={verify}
          onChange={e => setVerify(e.target.value.replace(/\D/g, '').slice(0, 12))}
          onKeyDown={e => { if (e.key === 'Enter' && verify.length >= 10) fetchReport(verify); }}
          inputMode="numeric" autoFocus placeholder="10-digit mobile number"
          style={{
            width: '100%', maxWidth: 280, padding: '14px 16px', fontSize: 18,
            textAlign: 'center', letterSpacing: 2, border: '2px solid #ced4da',
            borderRadius: 10, outline: 'none', boxSizing: 'border-box'
          }}
          onFocus={e => (e.target.style.borderColor = '#405189')}
          onBlur={e => (e.target.style.borderColor = '#ced4da')}
        />
        <button onClick={() => fetchReport(verify)} disabled={verify.length < 10}
                style={S.btn(verify.length >= 10)}>
          View My Report
        </button>
        <div style={S.note}>
          Your report is confidential.<br />Do not share this link with anyone.
        </div>
      </Shell>
    );

  if (state.phase === 'locked')
    return (
      <Shell accent="#b26a00" icon="⏱" title="Temporarily Locked"></Shell>
    );


  if (state.phase === 'nomobile')
    return (
      <Shell accent="#6c757d" icon="ⓘ" title="Verification Unavailable">
        <p style={{ color: '#5b6478', fontSize: 14.5, marginTop: 0 }}>{state.msg}</p>
        <div style={S.note}>Please collect your report.</div>
      </Shell>
    );

    

  if (state.phase === 'due')
    return (
      <Shell accent="#c9302c" icon="⚠️" title="Payment Pending">
        <div style={{ fontSize: 13, color: '#8a93a5', marginBottom: 4 }}>Outstanding amount</div>
        <div style={{ fontSize: 34, fontWeight: 700, color: '#c9302c', marginBottom: 16 }}>
          ₹{Number(state.due).toFixed(2)}
        </div>
        <p style={{ color: '#5b6478', fontSize: 14.5, margin: 0 }}>
          Kindly clear the payment to download your report.
        </p>
        <div style={S.note}>Please contact the Administrators.</div>
      </Shell>
    );

  if (state.phase === 'done')
    return (
      <Shell accent="#2e7d4f" icon="✓" title="Report Downloaded">
        <p style={{ color: '#5b6478', fontSize: 14.5, marginTop: 0 }}>
          Your report has been saved to this device.
        </p>
        <div style={S.note}>You may now close this page.</div>
      </Shell>
    );

  if (state.phase === 'expired')
    return (
      <Shell accent="#b26a00" icon="⏳" title="Link Expired">
        <p style={{ color: '#5b6478', fontSize: 14.5, marginTop: 0 }}>{state.msg}</p>
        <div style={S.note}>Please contact the Administrators for a new link.</div>
      </Shell>
    );

  return (
    <Shell accent="#6c757d" icon="ⓘ" title="Report Unavailable">
      <p style={{ color: '#5b6478', fontSize: 14.5, marginTop: 0 }}>
        {state.msg || 'Report not available.'}
      </p>
      <div style={S.note}>Please contact the Administrators.</div>
    </Shell>
  );
};

export default PublicReport;